import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Payment as PaymentIcon,
  Subscriptions as SubscriptionsIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  VpnKey as ApiKeyIcon,
  AccountCircle,
  Logout,
  Notifications,
  BugReport as DebugIcon,
} from '@mui/icons-material';
import { logout } from '../../store/slices/authSlice';
import { fetchNotifications } from '../../store/slices/notificationsSlice';
import { RootState, AppDispatch } from '../../store';
import { ROUTES } from '../../constants';
import NotificationCenter from '../NotificationCenter';

const drawerWidth = 180;

interface Props {
  children: React.ReactNode;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  badge?: number;
}

const menuItems: MenuItem[] = [
  {
    text: 'ダッシュボード',
    icon: <DashboardIcon />,
    path: ROUTES.DASHBOARD,
  },
  {
    text: '決済管理',
    icon: <PaymentIcon />,
    path: ROUTES.PAYMENTS,
  },
  {
    text: 'サブスクリプション',
    icon: <SubscriptionsIcon />,
    path: ROUTES.SUBSCRIPTIONS,
  },
  {
    text: '顧客管理',
    icon: <PeopleIcon />,
    path: ROUTES.CUSTOMERS,
  },
  {
    text: 'APIキー管理',
    icon: <ApiKeyIcon />,
    path: ROUTES.API_KEYS,
  },
  {
    text: '設定',
    icon: <SettingsIcon />,
    path: ROUTES.SETTINGS,
  },
  {
    text: 'デバッグ',
    icon: <DebugIcon />,
    path: ROUTES.DEBUG,
  },
];

const AdminLayout: React.FC<Props> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { unreadCount } = useSelector((state: RootState) => state.notifications);

  useEffect(() => {
    // Fetch notifications on mount and set up periodic refresh
    dispatch(fetchNotifications());
    
    const interval = setInterval(() => {
      dispatch(fetchNotifications());
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = async () => {
    console.log('🚪 Logging out user');
    await dispatch(logout());
    handleMenuClose();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ padding: 0 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleMenuItemClick(item.path)}
                sx={{ 
                  mx: 0.5,
                  mb: 0.5,
                  borderRadius: 1.5,
                  minHeight: 44,
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {item.badge ? (
                    <Badge badgeContent={item.badge} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    lineHeight: 1.2
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Full-width Header */}
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: 64,
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', px: 3 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f9fafb'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              fontSize: '1.125rem',
              color: '#111827'
            }}
          >
            GCペイメントシステム
          </Typography>
          
          {/* Notifications */}
          <Tooltip title="通知">
            <IconButton 
              onClick={handleNotificationClick}
              sx={{ 
                color: '#6b7280',
                mr: 1,
                '&:hover': {
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              <Badge 
                badgeContent={unreadCount} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    height: 18,
                    minWidth: 18
                  }
                }}
              >
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <IconButton
            onClick={handleMenuClick}
            sx={{ 
              ml: 1,
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f9fafb'
              }
            }}
          >
            <Avatar sx={{ 
              width: 32, 
              height: 32,
              fontSize: '0.875rem',
              backgroundColor: '#e5e7eb',
              color: '#374151'
            }}>
              {user?.name ? user.name.charAt(0) : <AccountCircle />}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                minWidth: 200
              }
            }}
          >
            <MenuItem disabled sx={{ opacity: 0.7 }}>
              <Typography variant="body2" color="text.secondary">
                {user?.email || 'guest@example.com'}
              </Typography>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem 
              onClick={handleLogout}
              sx={{
                '&:hover': {
                  backgroundColor: '#fef2f2',
                  color: '#dc2626'
                }
              }}
            >
              <ListItemIcon>
                <Logout fontSize="small" sx={{ color: 'inherit' }} />
              </ListItemIcon>
              ログアウト
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Left Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              top: 64,
              height: 'calc(100% - 64px)'
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              top: 64,
              height: 'calc(100% - 64px)',
              position: 'fixed'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { sm: '20px' },
          mt: '80px',
          p: 0,
          backgroundColor: '#fafafa',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {children}
      </Box>

      {/* Notification Center */}
      <NotificationCenter
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationClose}
      />
    </Box>
  );
};

export default AdminLayout;